import { Alert, Snackbar } from '@mui/material';
import type { AlertColor } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { apiErrorMessage } from '@/api/errors';
import { ToastContext } from '@/components/toast/context';
import type { Toast, ToastApi } from '@/components/toast/context';

/**
 * One place for the messages that follow an action.
 *
 * WHY THIS EXISTS. Every page rendered its own inline `<Alert>`, which worked where there was somewhere to
 * put one and failed where there was not: `PriceAction` sits inside a table row, so a failed pricing run
 * had nowhere to appear and was swallowed entirely — the click simply did nothing. Several of the inline
 * alerts also had no `onClose`, so a failure stayed on screen until the page was navigated away from.
 *
 * THE TIMINGS ARE DELIBERATE AND UNEQUAL. A success is an acknowledgement and four seconds is plenty; an
 * error is something to read and act on, and the sentences this application produces are whole sentences
 * ("QUO-2026-0001 is waiting on sign-off from Finance. It cannot be issued until that is given."). Ten
 * seconds is roughly twice as long as that takes to read, which is the point — an alert that vanishes
 * mid-sentence is worse than one that lingers.
 *
 * CLICKAWAY DOES NOT DISMISS. MUI's default closes a snackbar on any click anywhere, which means a message
 * disappears the moment somebody reaches for the thing it is telling them about. The reason is checked and
 * ignored, so only the close button or the timer ends it.
 *
 * NOT A REPLACEMENT FOR AN INLINE ALERT EVERYWHERE. A blocked Issue button explains itself on the page,
 * permanently, because it is a state rather than an event — see `QuotationPage`. This is for events.
 */
const DURATION: Record<AlertColor, number> = {
  success: 4000,
  info: 5000,
  warning: 8000,
  error: 10_000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => setToast({ message, severity: 'success' }),
      failure: (message) => setToast({ message, severity: 'error' }),
      failed: (error, fallback) =>
        setToast({ message: apiErrorMessage(error, fallback), severity: 'error' }),
    }),
    [],
  );

  const close = useCallback((_event: unknown, reason?: string) => {
    // See the class note: a click elsewhere must not take the message away.
    if (reason === 'clickaway') {
      return;
    }

    setToast(null);
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* MOUNTED ONLY WHEN THERE IS SOMETHING TO SAY. Keeping it mounted with an undefined child trips
          `exactOptionalPropertyTypes`, and the alternative — retaining the last message so the fade-out can
          play — is state machinery for an animation nobody asked for.

          Keyed on the message so a second toast restarts the timer rather than inheriting whatever was left
          of the first one's. */}
      {toast !== null && (
        <Snackbar
          key={toast.message}
          open
          autoHideDuration={DURATION[toast.severity]}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ maxWidth: 560 }}>
            {toast.message}
          </Alert>
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
}
